import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  CircularProgress,
  Skeleton,
  Container,
  Typography,
  useMediaQuery,
} from "@mui/material";

import CollectionSidebar from "../../components/CollectionSidebar/CollectionSidebar";
import UserHeader from "../../components/UserHeader/UserHeader";
import Followers from "./Followers";
import Following from "./Following";
import Collection from "./Collection/Collection";
import Settings from "./Settings";
import SettingsSidebar from "../../components/SettingsSidebar/SettingsSidebar";
import AmityProvider from "../../components/Bootstrap/AmityProvider";

import ShirtsService from "../../services/shirts/shirtsService";
import ShirtModelService from "../../services/shirts/shirtModelService";
import UserService from "../../services/userService";
import StatsService from "../../services/statsService";
import MenuService from "../../components/Menu/menuService";
import BackdropLoaderService from "../../components/BackdropLoader/backdropLoaderService";

import { Shirt } from "../../entities/Shirt";
import { User } from "../../entities/User";
import { Collection as CollectionEntity } from "../../entities/Collection";
import { Team } from "../../services/shirts/types";
import { StatElementProps } from "../../components/StatsSidebar/StatElement";
import { Dictionary } from "../../types/types";
import { RootState } from "../../state";
import { Actions } from "../../permission/constants";
import { roles } from "../../types/authTypes";
import {
  SORT_DIRECTION,
  SortElement,
} from "../../components/Buttons/SortButton/SortButton";
import { COLLECTION_SECTION } from "./Collection/Collection";
import { SETTINGS_TAB } from "./Settings";
import { SORT_VALUE } from "../../components/Buttons/SortButton/SortButtonForProfile";

export enum COLLECTION_TAB {
  COLLECTION = "collection",
  FOLLOWERS = "followers",
  FOLLOWING = "following",
  SETTINGS = "settings",
}

const MyProfile = () => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [ownPage, setOwnPage] = useState<boolean>(false);
  const [statsData, setStatsData] = useState<Array<StatElementProps>>([]);
  const [selectedTab, setSelectedTab] = useState<COLLECTION_TAB | undefined>(
    undefined
  );
  const [selectedCollectionSection, setSelectedCollectionSection] =
    useState<COLLECTION_SECTION>(COLLECTION_SECTION.OVERVIEW);
  const [selectedSettingsSection, setSelectedSettingsSection] =
    useState<SETTINGS_TAB>(SETTINGS_TAB.MY_ACCOUNT);
  const [totalShirts, setTotalShirts] = useState<number | undefined>(undefined);
  const [teamOptions, setTeamOptions] = useState<Array<Team>>([]);
  const [page, setPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number | undefined>(undefined);
  const [textFilter, setTextFilter] = useState<string | undefined>(undefined);
  const [teamFilter, setTeamFilter] = useState<Team | "allTeam">("allTeam");
  const [shirts, setShirts] = useState<Array<Shirt>>([]);
  const [orderBy, setOrderBy] = useState<SortElement>({
    value: SORT_VALUE.CLUB_NAME,
    direction: SORT_DIRECTION.ASC,
  });
  const [itemsPerPage, setItemsPerPage] = useState<number>(24);

  const isMobile = useMediaQuery("(max-width:768px)");

  const loginData = useSelector((state: RootState) => state.login);
  const dialogData = useSelector((state: RootState) => state.dialog);

  const { userId, tab, section } = useParams();

  const { getAllTeams } = ShirtModelService();
  const { getUserDetails } = UserService();
  const { getFinancialData } = StatsService();
  const { searchShirts } = ShirtsService();
  const { open: openBDL, close: closeBDL } = BackdropLoaderService();
  const { selectPage } = MenuService();

  const search = async () => {
    openBDL();
    let params: Dictionary<string | number> = {};

    if (textFilter) {
      params["q"] = textFilter;
    }
    if (orderBy) {
      params["order_by"] = orderBy.value;
      params["order_direction"] = orderBy.direction;
    }
    if (teamFilter && teamFilter !== "allTeam") {
      params["team_id"] = teamFilter.id;
    }
    if (page) {
      params["page"] = page;
    }

    params["perPage"] = page === 1 && ownPage ? itemsPerPage - 1 : itemsPerPage;

    if (user?.userId) {
      params["user_id"] = user.userId;
      const results = await searchShirts(params);
      setShirts(results.shirts);
      setLastPage(results.lastPage);

      // Cap totalShirts at 10 for non-premium users when viewing own profile
      const actualTotal = results.total || 0;
      const isPremium = loginData.role === roles.PREMIUM;
      const shouldCapTotal = ownPage && !isPremium;

      setTotalShirts(shouldCapTotal && actualTotal > 10 ? 10 : actualTotal);
    }

    closeBDL();
  };

  const initStatsData = () => {
    if (shirts.length > 0 && user?.userId) {
      const collectionInstance = new CollectionEntity(user.userId);
      getFinancialData(user.userId).then((results) => {
        if (results) {
          const stats: Array<StatElementProps> = [
            {
              currencySymbol: "€",
              title: "Purchasing value (" + totalShirts + " shirts)",
              value: parseFloat(results.total_purchase_price),
              action: Actions.VIEW_PURCHASING_VALUE,
              subject: collectionInstance,
            },
            {
              currencySymbol: "€",
              title: "Current value",
              value: parseFloat(results.total_price),
              action: Actions.VIEW_CURRENT_VALUE,
              subject: collectionInstance,
              help: (
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    fontStyle: "italic",
                  }}
                >
                  Our price predictor currently estimates the value of shirts by
                  analyzing and comparing them against the prices of thousands
                  of shirts sold over the past year. It considers factors such
                  as the player, team, and specific shirt details (e.g., whether
                  it is match-worn). At this stage, we rely on historical sales
                  data and mathematical modeling to calculate a shirt’s value.
                  <br />
                  <br />
                  Meanwhile, we’re training an AI model behind the scenes to
                  improve prediction accuracy. Once we’ve collected enough data,
                  the AI will fully take over the valuation process.
                  <br />
                  <br />
                  <strong>Note:</strong> BASIC Collectors can view the
                  purchasing value, the actual value and virtual profit only for
                  their first 10 uploaded shirts. VIP Collectors unlock these
                  values for all shirts in their collection.
                </Typography>
              ),
            },
            {
              currencySymbol: "€",
              title: "Virtual profit",
              value: parseFloat(results.total_profit),
              action: Actions.VIEW_VIRTUAL_PROFIT,
              subject: collectionInstance,
            },
          ];
          setStatsData(stats);
        }
      });
    }
  };

  useEffect(() => {
    selectPage("menu-myCollection");
    if (tab) {
      const auxTab = tab as COLLECTION_TAB;
      setSelectedTab(auxTab);
      if (section) {
        if (auxTab === COLLECTION_TAB.COLLECTION) {
          const auxSection = section as COLLECTION_SECTION;
          setSelectedCollectionSection(auxSection);
        } else {
          if (auxTab === COLLECTION_TAB.SETTINGS) {
            const auxSection = section as SETTINGS_TAB;
            setSelectedSettingsSection(auxSection);
          }
        }
      }
    } else {
      setSelectedTab(COLLECTION_TAB.COLLECTION);
    }
  });

  useEffect(() => {
    if (userId !== undefined && parseInt(userId) !== loginData.userId) {
      getUserDetails(parseInt(userId)).then((result) => {
        if (result) {
          setUser(result);
          setOwnPage(false);
        }
      });
    } else {
      setUser(loginData);
      setOwnPage(true);
    }
  }, []);

  useEffect(() => {
    setItemsPerPage(isMobile ? 10 : 24);
  }, [isMobile]);

  useEffect(() => {
    if (userId !== undefined && parseInt(userId) !== loginData.userId) {
      getUserDetails(parseInt(userId)).then((result) => {
        if (result) {
          setUser(result);
          setOwnPage(false);
        }
      });
    } else {
      setUser(loginData);
      setOwnPage(true);
    }
  }, [userId]);

  useEffect(() => {
    if (userId !== undefined && parseInt(userId) !== loginData.userId) {
      getUserDetails(parseInt(userId)).then((result) => {
        if (result) {
          setUser(result);
          setOwnPage(false);
        }
      });
    } else {
      setUser(loginData);
      setOwnPage(true);
    }
  }, [loginData]);

  useEffect(() => {
    getAllTeams().then((teams) => {
      if (teams) {
        setTeamOptions(teams);
      }
    });
  }, []);

  useEffect(() => {
    const lastDialog = dialogData.dialogs[dialogData.dialogs.length - 1];
    if (lastDialog?.open === false && lastDialog?.reload === true) {
      search();
    }
  }, [dialogData]);

  useEffect(() => {
    if (!user) return;

    const timeout = setTimeout(() => {
      search();
    }, 200); // slight delay to batch updates

    return () => clearTimeout(timeout);
  }, [user, orderBy, page, itemsPerPage, teamFilter]);

  useEffect(() => {
    if (userId !== undefined && parseInt(userId) !== loginData.userId) {
      getUserDetails(parseInt(userId)).then((result) => {
        if (result) {
          setUser(result);
          setOwnPage(false);
        }
      });
    } else {
      setUser(loginData);
      setOwnPage(true);
    }
  }, []);

  useEffect(() => {
    initStatsData();
  }, [shirts, user]);

  return (
    <Container
      maxWidth={false}
      sx={{
        backgroundColor: "#f9f9f9",
        padding: "1rem 0rem 1.5rem 0rem",
      }}
      className="profile-section"
      aria-label="MyProfileContainer-Lv1"
    >
      {selectedTab ? (
        <Container
          maxWidth="lg"
          sx={{ paddingX: { sm: "0px !important" } }}
          aria-label="MyProfileContainer-Lv2"
        >
          {user ? (
            <AmityProvider>
              <UserHeader
                user={user}
                setTab={setSelectedTab}
                totalShirts={totalShirts}
                selectedTab={selectedTab}
              />
            </AmityProvider>
          ) : (
            <Skeleton variant="rectangular" height={200} animation="wave" />
          )}
          <Box
            columnGap={2}
            sx={{
              display: "flex",
              paddingTop: "1rem",
              flexDirection: { md: "row", xs: "column" },
            }}
            aria-label="CollectionBody"
          >
            <Box flex={9}>
              {selectedTab === COLLECTION_TAB.COLLECTION && (
                <>
                  {user && (
                    <Collection
                      setOrderBy={setOrderBy}
                      setTeam={setTeamFilter}
                      teamOptions={teamOptions}
                      lastPage={lastPage}
                      page={page}
                      setPage={setPage}
                      shirts={shirts}
                      parentPath={
                        "/my-profile/" + String(user.userId) + "/" + selectedTab
                      }
                      selectedTab={selectedCollectionSection}
                      itemsPerPage={itemsPerPage}
                      setItemsPerPage={setItemsPerPage}
                      user={user}
                      ownPage={ownPage}
                    />
                  )}
                </>
              )}
              {selectedTab === COLLECTION_TAB.FOLLOWERS && (
                <>{user && <Followers user={user} />}</>
              )}
              {selectedTab === COLLECTION_TAB.FOLLOWING && (
                <>{user && <Following user={user} />}</>
              )}
              {selectedTab === COLLECTION_TAB.SETTINGS && (
                <>
                  {user && (
                    <Settings
                      parentPath={
                        "/my-profile/" + String(user.userId) + "/" + selectedTab
                      }
                      selectedTab={selectedSettingsSection}
                    />
                  )}
                </>
              )}
            </Box>
            <Box flex={3}>
              {selectedTab === COLLECTION_TAB.SETTINGS && <SettingsSidebar />}
              {selectedTab === COLLECTION_TAB.COLLECTION && (
                <CollectionSidebar
                  stats={statsData}
                  setTextFilter={setTextFilter}
                  search={search}
                  contentVisiable={
                    selectedTab === COLLECTION_TAB.COLLECTION &&
                    selectedCollectionSection === COLLECTION_SECTION.OVERVIEW
                  }
                />
              )}
            </Box>
          </Box>
        </Container>
      ) : (
        <CircularProgress />
      )}
    </Container>
  );
};

export default MyProfile;
